import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { DatabaseChangeType } from '@databases/types/redux';
import { fieldTypeSupportsDefaultValue } from '@databases/utils/database/field-types';
import { getDatabaseFieldRequired } from '@databases/utils/database/utils';
import React, { type FunctionComponent, useCallback } from 'react';

import DefaultValueSetting from './DefaultValueSetting';
import { getFieldSettingsOptionsByType } from './FieldSettingsOptionsByType';
import AllowMultiToggle from './shared/AllowMultiToggle';
import { getAllowMultiToggleMessages } from './shared/getAllowMultiToggleMessages';

const containerStyles = xcss({
  borderTop: `1px solid ${token('color.border')}`,
  paddingInline: 'space.200',
  paddingBlockStart: 'space.200',
  paddingBlockEnd: 'space.100'
});

type FieldSettingsMenuContentProps = {
  contentId: string;
  fieldId: string;
};

const FieldSettingsMenuContent: FunctionComponent<FieldSettingsMenuContentProps> = ({
  contentId,
  fieldId
}) => {
  const databaseDraft = defaultStoreUtils.useSelector((state) =>
    selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: true
    })
  );

  const field = getDatabaseFieldRequired(databaseDraft, fieldId);
  const dispatch = defaultStoreUtils.useDispatch();
  const handleOnChangeAllowMulti = useCallback(
    (isMulti: boolean) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_IS_MULTI,
          fieldId,
          isMulti
        })
      );
    },
    [contentId, dispatch, fieldId]
  );

  const allowMultiMessages = getAllowMultiToggleMessages(field.type);
  const fieldSettingsOptions = getFieldSettingsOptionsByType(contentId, field);
  const shouldRenderDefaultValue = fieldTypeSupportsDefaultValue(field.type);

  return (
    <Stack space="space.150" xcss={containerStyles}>
      {fieldSettingsOptions}
      <Stack space="space.150">
        {allowMultiMessages && (
          <AllowMultiToggle
            labelMessage={allowMultiMessages.labelMessage}
            onChange={handleOnChangeAllowMulti}
            tooltipMessage={allowMultiMessages.tooltipMessage}
            value={Boolean(field.settings.multi)}
          />
        )}
        {shouldRenderDefaultValue && (
          <Box paddingBlockEnd="space.025">
            <DefaultValueSetting
              contentId={contentId}
              database={databaseDraft}
              field={field}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default FieldSettingsMenuContent;
